import {AgGridReact} from 'ag-grid-react'
import {Box, IconButton} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {useNavigate} from 'react-router-dom'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import  {Chip} from '@origamirisk/origami-storybook'
import {formatFullTimestamp} from '../../utils/dateFormatter'

export interface BulkRatingRequest {
    requestId: string
    status: string
    totalCount: number
    requestPayload: {
        states?: string[],
        proposalIds?: number[],

    }
    progress: number
    createdAt?: string
    completedAt?: string
}

interface BulkRatingGridProps {
    bulkRequests: BulkRatingRequest[]
    onDelete?: (requestId: string) => void
}

function formatDuration(createdAt?: string, completedAt?: string): string {
    if (!createdAt || !completedAt) return '-'

    const start = new Date(createdAt).getTime()
    const end = new Date(completedAt).getTime()
    const durationMs = end - start

    if (durationMs < 0) return '-'

    const ms = durationMs % 1000
    const seconds = Math.floor(durationMs / 1000) % 60
    const minutes = Math.floor(durationMs / 60000) % 60
    const hours = Math.floor(durationMs / 3600000)

    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s ${ms}ms`
    } else if (minutes > 0) {
        return `${minutes}m ${seconds}s ${ms}ms`
    } else {
        return `${seconds}s ${ms}ms`
    }
}

function BulkRatingGrid({bulkRequests, onDelete}: BulkRatingGridProps) {
    const navigate = useNavigate()

    const handleRequestIdClick = (requestId: string) => {
        navigate(`/bulk-rating/${requestId}`)
    }

    const handleDelete = (requestId: string) => {
        if (onDelete) {
            onDelete(requestId)
        }
    }
    return (
        <Box sx={{height: 'calc(100vh - 200px)', width: '100%'}}>
            <div className="ag-theme-balham" style={{height: '100%', width: '100%'}}>
                <AgGridReact
                    rowData={bulkRequests}
                    rowHeight={34}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    autoSizeStrategy={{type: 'fitGridWidth', defaultMinWidth: 100}}
                    columnDefs={[
                        {
                            field: 'requestId',
                            headerName: 'Request ID',
                            flex: 1,
                            minWidth: 150,
                            cellRenderer: (params: any) => (
                                <span
                                    style={{color: '#1976d2', cursor: 'pointer', textDecoration: 'underline'}}
                                    onClick={() => handleRequestIdClick(params.value)}
                                >
                                    {params.value}
                                </span>
                            )
                        },
                        {
                            field: 'createdAt',
                            headerName: 'Created At',
                            flex: 1,
                            valueFormatter: (params: any) => formatFullTimestamp(params.data?.createdAt),
                        },
                        {field: 'policySetName', headerName: 'Rating Program', flex: 1.5, minWidth: 150},

                        {field: 'status', headerName: 'Status', flex: 1, minWidth: 120},
                        {
                            field: 'requestPayload',
                            headerName: 'States',
                            flex: 1,
                            minWidth: 120,
                            cellRenderer: (params: any) => (

                                (params.value.states && (
                                    params.value.states.map((state: string) => (

                                        <Chip
                                            color="default"
                                            label={state}
                                            size="md"
                                            variant="filled"
                                        />

                                    ))
                                )) || (params.value.proposalIds && (
                                    params.value.proposalIds.map((state: string) => (
                                        <Chip
                                            color="default"
                                            label={state}
                                            size="md"
                                            variant="filled"
                                        />

                                    ))
                                ))
                            )
                        },
                        {field: 'totalCount', headerName: 'Total Count', flex: 0.8, minWidth: 100},
                        {
                            headerName: 'Duration',
                            flex: 1,
                            minWidth: 120,
                            valueGetter: (params: any) => formatDuration(params.data?.createdAt, params.data?.completedAt)
                        },
                        {
                            headerName: '',
                            width: 60,
                            minWidth: 60,
                            maxWidth: 60,
                            sortable: false,
                            filter: false,
                            resizable: false,
                            cellRenderer: (params: any) => (
                                <IconButton
                                    size="small"
                                    onClick={() => handleDelete(params.data?.requestId)}
                                    sx={{padding: '4px'}}
                                >
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            )
                        }
                    ]}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                    }}
                    pagination={true}
                />
            </div>
        </Box>
    )
}

export default BulkRatingGrid
